import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import WordMultiSearchSelect from '../../components/forms/WordMultiSearchSelect';
import LessonWordSelect from '../../components/forms/LessonWordSelect';
import language from '../../variables/language';
import ActiveTasksContext from '../../providers/ActiveTasksContext';
import IconChevronDown from '../../components/icons/IconChevronDown';

const stopProp = (e) => e.stopPropagation();
const MINIMIZE_ANIMATION_MS = 300;

export default function QuizGenerationModal({ isOpen, studentId, studentLabel, onClose, onGenerated }) {
   const lang = language[language.lang];
   const t = lang.modals.quizGeneration;
   const { startQuizGenerationTask, tasks } = useContext(ActiveTasksContext);

   const [lessonId, setLessonId] = useState('');
   const [additionalWords, setAdditionalWords] = useState([]);
   const [studentWords, setStudentWords] = useState([]);
   const [difficulty, setDifficulty] = useState('');
   const [questionCount, setQuestionCount] = useState('');
   const [optionsPerQuestion, setOptionsPerQuestion] = useState('');
   const [textAnswerRatio, setTextAnswerRatio] = useState('');
   const [useAiMode, setUseAiMode] = useState('');
   const [creativityMode, setCreativityMode] = useState('');
   const [autoNaming, setAutoNaming] = useState(true);
   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [generating, setGenerating] = useState(false);
   const [taskId, setTaskId] = useState(null);
   const [minimizing, setMinimizing] = useState(false);
   const [error, setError] = useState(null);

   useEffect(() => {
      if(!isOpen) {
         return;
      }
      setLessonId('');
      setAdditionalWords([]);
      setDifficulty('');
      setQuestionCount('');
      setOptionsPerQuestion('');
      setTextAnswerRatio('');
      setUseAiMode('');
      setCreativityMode('');
      setAutoNaming(true);
      setTitle('');
      setDescription('');
      setGenerating(false);
      setTaskId(null);
      setMinimizing(false);
      setError(null);

      axios.get('/api/wordstudents', { params: { student: studentId }, withCredentials: true })
         .then(response => setStudentWords(Array.isArray(response.data) ? response.data : []))
         .catch(() => setStudentWords([]));
   }, [isOpen, studentId]);

   // While the task this modal started is still tracked in ActiveTasksContext
   // (i.e. the teacher never minimized it, they just waited), let its own
   // completion drive the modal's own success/error UI - the axios call
   // itself lives in ActiveTasksProvider now (see startQuizGenerationTask),
   // not here, so this is the only way this component finds out it settled.
   useEffect(() => {
      if(!taskId) {
         return;
      }
      const task = tasks.find(t => t.id === taskId);
      if(!task) {
         return;
      }
      if(task.status === 'success') {
         onGenerated();
         onClose();
      } else if(task.status === 'error') {
         setError(task.errorMessage || t.genericError);
         setGenerating(false);
         setTaskId(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [tasks, taskId]);

   if(!isOpen) {
      return '';
   }

   const toggleStudentWord = (word) => {
      setAdditionalWords(prev => prev.some(w => w.id === word.id)
         ? prev.filter(w => w.id !== word.id)
         : [...prev, { id: word.id, word: word.word }]);
   }

   const handleGenerate = () => {
      if(generating) {
         return;
      }
      // A source is always required - either words picked from the
      // student's own list and/or searched in fresh, OR a lesson (the
      // picker only ever lists lessons that already have words attached -
      // see LessonWordSelect - so picking one is itself a guarantee of a
      // non-empty source, no separate "and at least one word" rule needed
      // on top of it).
      if(additionalWords.length === 0 && !lessonId) {
         setError(t.sourceRequiredError);
         return;
      }
      setError(null);

      const request = { studentId: Number(studentId) };
      if(lessonId) request.lessonId = Number(lessonId);
      if(additionalWords.length > 0) request.additionalWordIds = additionalWords.map(w => w.id);
      if(difficulty) request.difficulty = difficulty;
      if(questionCount) request.questionCount = Number(questionCount);
      if(optionsPerQuestion) request.optionsPerQuestion = Number(optionsPerQuestion);
      // 0 is a meaningful, deliberate override ("force zero text-answer
      // questions") - a truthy check like the fields above would wrongly
      // treat it as "unset" and silently fall back to the config default.
      if(textAnswerRatio !== '') request.textAnswerRatio = Number(textAnswerRatio);
      if(useAiMode !== '') request.useAi = useAiMode === 'true';
      if(creativityMode) request.creativityMode = creativityMode;
      request.autoNaming = autoNaming;
      if(!autoNaming) {
         if(title.trim()) request.title = title.trim();
         if(description.trim()) request.description = description.trim();
      }

      const id = startQuizGenerationTask({ studentId, studentLabel, request });
      setTaskId(id);
      setGenerating(true);
   }

   // Kicks off the collapse animation, then hands control back to the
   // parent (onClose) once it's finished playing - the in-flight task keeps
   // running regardless (it lives in ActiveTasksProvider, not this
   // component), this is purely a visual handoff to the tray.
   const handleMinimize = () => {
      setMinimizing(true);
      setTimeout(() => onClose(), MINIMIZE_ANIMATION_MS);
   }

   return (
      <div className='modal-menu' onClick={onClose} style={{display:'flex'}}>
         <div className={'modal-window quiz-generation-modal-window' + (minimizing ? ' minimizing' : '')} onClick={stopProp}>
            <h3 className='modal-w-title'>{t.title}</h3>

            {generating ? (
               <div className='quiz-generation-modal-body'>
                  <div className='quiz-generation-progress-panel'>
                     <span className='quiz-generation-progress-spinner' />
                     <span className='quiz-generation-progress-label'>{t.generating}</span>
                  </div>
                  {error ? <p className='quiz-generation-modal-error'>{error}</p> : ''}
                  <div className='student-card-form-footer'>
                     <button type='button' className='student-card-save-btn' onClick={handleMinimize} title={t.minimizeTooltip}>
                        <IconChevronDown style={{marginRight:4}} />
                        {t.minimizeTooltip}
                     </button>
                  </div>
               </div>
            ) : (
            <div className='quiz-generation-modal-body'>
               <p className='quiz-generation-source-hint'>{t.sourceHint}</p>

               <label className='quiz-generation-modal-field'>
                  <span>{t.studentWordsLabel}</span>
                  {studentWords.length === 0 ? (
                     <p className='quiz-generation-empty-hint'>{t.noStudentWords}</p>
                  ) : (
                     <div className='quiz-generation-student-words'>
                        {studentWords.map(ws => (
                           <span key={ws.word.id}
                              className={'quiz-generation-word-pill' + (additionalWords.some(w => w.id === ws.word.id) ? ' selected' : '')}
                              onClick={() => toggleStudentWord(ws.word)}>
                              {ws.word.word}
                           </span>
                        ))}
                     </div>
                  )}
               </label>

               <label className='quiz-generation-modal-field'>
                  <span>{t.additionalWordsLabel}</span>
                  <WordMultiSearchSelect value={additionalWords} onChange={setAdditionalWords}
                     placeholder={t.additionalWordsPlaceholder} />
               </label>

               <label className='quiz-generation-modal-field'>
                  <span>{t.lessonLabel}</span>
                  <LessonWordSelect studentId={studentId} value={lessonId} onChange={setLessonId} placeholder={t.lessonDefault} />
               </label>

               <div className='quiz-generation-modal-row'>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.difficultyLabel}</span>
                     <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                        <option value=''>{t.difficultyDefault}</option>
                        <option value='EASY'>{t.difficultyEasy}</option>
                        <option value='MEDIUM'>{t.difficultyMedium}</option>
                        <option value='HARD'>{t.difficultyHard}</option>
                     </select>
                  </label>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.useAiLabel}</span>
                     <select value={useAiMode} onChange={(e) => setUseAiMode(e.target.value)}>
                        <option value=''>{t.useAiDefault}</option>
                        <option value='true'>{t.useAiOn}</option>
                        <option value='false'>{t.useAiOff}</option>
                     </select>
                  </label>
               </div>

               {useAiMode !== 'false' ? (
                  <label className='quiz-generation-modal-field'>
                     <span>{t.creativityLabel}</span>
                     <select value={creativityMode} onChange={(e) => setCreativityMode(e.target.value)}>
                        <option value=''>{t.creativityDefault}</option>
                        <option value='CONSERVATIVE'>{lang.quizGenerationConfigPage.creativityConservative}</option>
                        <option value='BALANCED'>{lang.quizGenerationConfigPage.creativityBalanced}</option>
                        <option value='CREATIVE'>{lang.quizGenerationConfigPage.creativityCreative}</option>
                     </select>
                  </label>
               ) : ''}

               <div className='quiz-generation-modal-row'>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.questionCountLabel}</span>
                     <input type='number' min='1' placeholder={t.questionCountPlaceholder}
                        value={questionCount} onChange={(e) => setQuestionCount(e.target.value)} />
                  </label>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.optionsPerQuestionLabel}</span>
                     <input type='number' min='2' placeholder={t.optionsPerQuestionPlaceholder}
                        value={optionsPerQuestion} onChange={(e) => setOptionsPerQuestion(e.target.value)} />
                  </label>
               </div>

               <label className='quiz-generation-modal-field'>
                  <span>{t.textAnswerRatioLabel}</span>
                  <input type='number' min='0' max='100' placeholder={t.textAnswerRatioPlaceholder}
                     value={textAnswerRatio} onChange={(e) => setTextAnswerRatio(e.target.value)} />
               </label>

               <label className='quiz-generation-modal-toggle-row'>
                  <input type='checkbox' checked={autoNaming} onChange={(e) => setAutoNaming(e.target.checked)} />
                  <span>{t.autoNamingLabel}</span>
               </label>

               {!autoNaming ? (
                  <>
                     <label className='quiz-generation-modal-field'>
                        <span>{t.titleLabel}</span>
                        <input type='text' placeholder={t.titlePlaceholder}
                           value={title} onChange={(e) => setTitle(e.target.value)} />
                     </label>
                     <label className='quiz-generation-modal-field'>
                        <span>{t.descriptionLabel}</span>
                        <input type='text' placeholder={t.descriptionPlaceholder}
                           value={description} onChange={(e) => setDescription(e.target.value)} />
                     </label>
                  </>
               ) : ''}

               {error ? <p className='quiz-generation-modal-error'>{error}</p> : ''}

               <div className='student-card-form-footer'>
                  <div className='student-card-discard-link' onClick={onClose}>{lang.cancel}</div>
                  <button type='button' className='student-card-save-btn' disabled={generating} onClick={handleGenerate}>
                     {generating ? t.generating : t.generateBtn}
                  </button>
               </div>
            </div>
            )}
         </div>
      </div>
   );
}
